import React, { useEffect, useRef, useState } from 'react';
import StatBox from '../ui/StatBox';
import { GameIntro, GameOver, GameState, LevelPicker, isTypingKey, useLevelProgress, useSaveGameResult } from './GameShell';
import { playMusicNote, playWrongSound } from '../../utils/audio';
import { FINGER_COLORS, KEY_TO_FINGER } from '../../constants';
import { User } from '../../types';

// Each song is a level. Melodies are written as "pitch[octave][:beats]" tokens
// (octave defaults to 4, beats to 1). The song's pitches, low to high, are spread
// across `keys` from left to right, so the lanes work like a little piano.
interface Song {
  name: string;
  icon: string;
  by: string;
  row: string;
  keys: string;
  bpm: number;
  endBpm?: number; // speeds up from bpm to endBpm over the whole song
  repeat?: number;
  notes: string;
}

// Ordered easiest to hardest; a student's saved level is an index into this list.
const SONGS: Song[] = [
  { name: 'Hot Cross Buns', icon: '🍞', by: 'Traditional', row: 'Home row', keys: 'dfj', bpm: 84, repeat: 2,
    notes: 'E D C:2 E D C:2 C:.5 C:.5 C:.5 C:.5 D:.5 D:.5 D:.5 D:.5 E D C:2' },
  { name: "Mary's Lamb", icon: '🐑', by: 'Traditional', row: 'Home row', keys: 'dfjk', bpm: 92, repeat: 2,
    notes: 'E D C D E E E:2 D D D:2 E G G:2 E D C D E E E E D D E D C:4' },
  { name: 'Row Your Boat', icon: '🚣', by: 'Traditional', row: 'Home row', keys: 'sdfjkl', bpm: 190, repeat: 2,
    notes: 'C:3 C:3 C:2 D E:3 E:2 D E:2 F G:6 C5 C5 C5 G G G E E E C C C G:2 F E:2 D C:6' },
  { name: 'Twinkle Star', icon: '⭐', by: 'Traditional', row: 'Home row', keys: 'sdfjkl', bpm: 100,
    notes: 'C C G G A A G:2 F F E E D D C:2 G G F F E E D:2 G G F F E E D:2 C C G G A A G:2 F F E E D D C:2' },
  { name: 'Old MacDonald', icon: '🐮', by: 'Traditional', row: 'Home row', keys: 'sdfjk', bpm: 120, repeat: 2,
    notes: 'C C C G3 A3 A3 G3:2 E E D D C:3 G3 C C C G3 A3 A3 G3:2 E E D D C:3' },
  { name: 'Frère Jacques', icon: '🔔', by: 'Traditional', row: 'Home row', keys: 'asdfjkl', bpm: 108, repeat: 2,
    notes: 'C D E C C D E C E F G:2 E F G:2 G:.5 A:.5 G:.5 F:.5 E C G:.5 A:.5 G:.5 F:.5 E C C G3 C:2 C G3 C:2' },
  { name: 'London Bridge', icon: '🌉', by: 'Traditional', row: 'Home row', keys: 'sdfjkl', bpm: 112, repeat: 2,
    notes: 'G:1.5 A:.5 G F E F G:2 D E F:2 E F G:2 G:1.5 A:.5 G F E F G:2 D:2 G:2 E C:3' },
  { name: 'Ode to Joy', icon: '🎻', by: 'Beethoven', row: 'Top row', keys: 'wertyu', bpm: 112,
    notes: 'E E F G G F E D C C D E E:1.5 D:.5 D:2 E E F G G F E D C C D E D:1.5 C:.5 C:2 '
      + 'D D E C D E:.5 F:.5 E C D E:.5 F:.5 E D C D G3:2 E E F G G F E D C C D E D:1.5 C:.5 C:2' },
  { name: 'Marching Saints', icon: '🎺', by: 'Traditional', row: 'Top row', keys: 'ertyu', bpm: 140,
    notes: 'C E F G:4 C E F G:4 C E F G:2 E:2 C:2 E:2 D:4 E E D C:3 C E:2 G:2 G F:3 E F G:2 E:2 C:2 D:2 C:4' },
  { name: 'Jingle Bells', icon: '🛷', by: 'J. Pierpont', row: 'Top row', keys: 'ertyu', bpm: 132,
    notes: 'E E E:2 E E E:2 E G C:1.5 D:.5 E:4 F F F:1.5 F:.5 F E E E:.5 E:.5 E D D E D:2 G:2 '
      + 'E E E:2 E E E:2 E G C:1.5 D:.5 E:4 F F F F F E E E:.5 E:.5 G G F D C:4' },
  { name: 'Yankee Doodle', icon: '🎩', by: 'Traditional', row: 'Bottom row', keys: 'zxcvbnm', bpm: 160, repeat: 2,
    notes: 'C C D E C E D G3 C C D E C:2 B3:2 C C D E F E D C B3 G3 A3 B3 C:2 C:2' },
  { name: 'Happy Birthday', icon: '🎂', by: 'Hill sisters', row: 'Bottom row', keys: 'zxcvbnm,', bpm: 120, repeat: 2,
    notes: 'G3:.5 G3:.5 A3 G3 C B3:2 G3:.5 G3:.5 A3 G3 D C:2 G3:.5 G3:.5 G E C B3 A3:2 F:.5 F:.5 E C D C:3' },
  { name: 'Für Elise', icon: '🎹', by: 'Beethoven', row: 'Home row + G H', keys: 'asdfghjkl', bpm: 210, repeat: 2,
    notes: 'E5 D#5 E5 D#5 E5 B4 D5 C5 A4:3 C4 E4 A4 B4:3 E4 G#4 B4 C5:3 E4 E5 D#5 E5 D#5 E5 B4 D5 C5 A4:3 C4 E4 A4 B4:3 E4 C5 B4 A4:4' },
  { name: 'Minuet in G', icon: '💃', by: 'Petzold (Bach notebook)', row: 'Bottom row', keys: 'zxcvbnm,.', bpm: 112,
    notes: 'D5 G4:.5 A4:.5 B4:.5 C5:.5 D5 G4 G4 E5 C5:.5 D5:.5 E5:.5 F#5:.5 G5 G4 G4 C5 D5:.5 C5:.5 B4:.5 A4:.5 '
      + 'B4 C5:.5 B4:.5 A4:.5 G4:.5 F#4 G4:.5 A4:.5 B4:.5 G4:.5 A4:3 D5 G4:.5 A4:.5 B4:.5 C5:.5 D5 G4 G4 '
      + 'E5 C5:.5 D5:.5 E5:.5 F#5:.5 G5 G4 G4 C5 D5:.5 C5:.5 B4:.5 A4:.5 B4 C5:.5 B4:.5 A4:.5 G4:.5 A4 B4:.5 A4:.5 G4:.5 F#4:.5 G4:3' },
  { name: 'Greensleeves', icon: '🍃', by: 'Traditional', row: 'Top row', keys: 'qwertyuiop', bpm: 132, repeat: 2,
    notes: 'A4 C5:2 D5 E5:1.5 F5:.5 E5 D5:2 B4 G4:1.5 A4:.5 B4 C5:2 A4 A4:1.5 G#4:.5 A4 B4:2 G#4 E4:2 '
      + 'A4 C5:2 D5 E5:1.5 F5:.5 E5 D5:2 B4 G4:1.5 A4:.5 B4 C5:1.5 B4:.5 A4 G#4:1.5 F#4:.5 G#4 A4:3' },
  { name: 'Mountain King', icon: '🏔️', by: 'Grieg', row: 'Top row', keys: 'qwertyuio', bpm: 200, endBpm: 300, repeat: 3,
    notes: 'A3 B3 C D E C E:2 D# B3 D#:2 D Bb3 D:2 A3 B3 C D E C E A G E C E G:4' },
];

const NOTE_STEPS: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const NAME_SIGN: Record<string, string> = { '#': '♯', b: '♭' };

interface SongNote { midi: number; label: string; beat: number; beats: number }

const parseSong = (song: Song) => {
  const one: { midi: number; label: string; beats: number }[] = [];
  for (const tok of song.notes.trim().split(/\s+/)) {
    const m = /^([A-G])([#b]?)(\d?)(?::([\d.]+))?$/.exec(tok);
    if (!m) continue;
    const midi = 12 * (Number(m[3] || 4) + 1) + NOTE_STEPS[m[1]] + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0);
    one.push({ midi, label: m[1] + (NAME_SIGN[m[2]] ?? ''), beats: Number(m[4] || 1) });
  }
  const notes: SongNote[] = [];
  let beat = 0;
  for (let r = 0; r < (song.repeat ?? 1); r++) {
    for (const n of one) { notes.push({ ...n, beat }); beat += n.beats; }
  }
  const pitches = [...new Set(notes.map(n => n.midi))].sort((a, b) => a - b);
  return { notes, totalBeats: beat, pitches };
};

const midiToFreq = (midi: number) => 440 * Math.pow(2, (midi - 69) / 12);

// Seconds from the first note to `beat`, following the song's tempo ramp.
const beatTime = (song: Song, totalBeats: number, beat: number) => {
  const a = 60 / song.bpm, b = 60 / (song.endBpm ?? song.bpm);
  // Seconds-per-beat changes linearly from a to b, so integrate it.
  return a * beat + ((b - a) * beat * beat) / (2 * totalBeats);
};

const HEIGHT = 440;
const HIT_Y = HEIGHT - 70;
const FALL_SECONDS = 2.2;
const PX_PER_MS = HIT_Y / (FALL_SECONDS * 1000);
const LEAD_MS = FALL_SECONDS * 1000 + 800;
const PERFECT_MS = 70;
const GOOD_MS = 160;
const PASS = 60; // accuracy % needed to unlock the next song

interface Note { id: number; lane: number; hitAt: number; ms: number; freq: number; label: string; result: 'perfect' | 'good' | 'miss' | null }
interface Popup { id: number; lane: number; text: string; at: number }

const newState = (songIdx: number) => ({
  song: songIdx, start: 0, now: 0, end: 0, notes: [] as Note[], popups: [] as Popup[], pressed: new Map<number, number>(),
  score: 0, combo: 0, bestCombo: 0, perfect: 0, good: 0, missed: 0, strayPresses: 0, newUnlock: false,
});

const starsFor = (accuracy: number) => (accuracy >= 90 ? 3 : accuracy >= 75 ? 2 : accuracy >= PASS ? 1 : 0);

const KeyBeatsGame: React.FC<{ user?: User }> = ({ user }) => {
  const [gameState, setGameState] = useState<GameState>('ready');
  const [unlocked, unlock, levelsLoaded] = useLevelProgress('key-beats', user);
  const [songIdx, setSongIdx] = useState(() => Math.min(unlocked, SONGS.length - 1));
  useEffect(() => { if (levelsLoaded) setSongIdx(Math.min(unlocked, SONGS.length - 1)); }, [levelsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const [previewing, setPreviewing] = useState(false);
  const previewTimers = useRef<number[]>([]);
  const [, setFrame] = useState(0);
  const g = useRef(newState(0));

  const stopPreview = () => {
    previewTimers.current.forEach(t => clearTimeout(t));
    previewTimers.current = [];
    setPreviewing(false);
  };
  useEffect(() => () => previewTimers.current.forEach(t => clearTimeout(t)), []);

  // Plays the opening of a song so players know the tune before they start.
  const preview = (idx: number) => {
    stopPreview();
    const song = SONGS[idx];
    const { notes, totalBeats } = parseSong(song);
    const opening = notes.filter(n => n.beat < 16);
    setPreviewing(true);
    for (const n of opening) {
      const at = beatTime(song, totalBeats, n.beat) * 1000;
      const ms = beatTime(song, totalBeats, n.beat + n.beats) * 1000 - at;
      previewTimers.current.push(window.setTimeout(() => playMusicNote(midiToFreq(n.midi), ms / 1000 * 0.9, 0.25), at));
    }
    const last = opening[opening.length - 1];
    const doneAt = beatTime(song, totalBeats, last.beat + last.beats) * 1000 + 300;
    previewTimers.current.push(window.setTimeout(() => setPreviewing(false), doneAt));
  };

  const startGame = (idx = songIdx) => {
    stopPreview();
    const song = SONGS[idx];
    const { notes, totalBeats, pitches } = parseSong(song);
    const now = performance.now();
    const s = newState(idx);
    s.start = now;
    s.now = now;
    s.notes = notes.map((n, i) => {
      const t = beatTime(song, totalBeats, n.beat) * 1000;
      return {
        id: i + 1, lane: pitches.indexOf(n.midi) % song.keys.length, hitAt: now + LEAD_MS + t,
        ms: beatTime(song, totalBeats, n.beat + n.beats) * 1000 - t, freq: midiToFreq(n.midi), label: n.label, result: null,
      };
    });
    const last = s.notes[s.notes.length - 1];
    s.end = last.hitAt + last.ms;
    g.current = s;
    setSongIdx(idx);
    setGameState('playing');
  };

  useSaveGameResult(user, 'key-beats', gameState, g.current.score);

  const judge = (s: typeof g.current, note: Note, result: 'perfect' | 'good' | 'miss') => {
    note.result = result;
    if (result === 'miss') {
      s.missed++;
      s.combo = 0;
    } else {
      s.combo++;
      s.bestCombo = Math.max(s.bestCombo, s.combo);
      const multiplier = Math.min(4, 1 + Math.floor(s.combo / 10));
      s.score += (result === 'perfect' ? 100 : 50) * multiplier;
      if (result === 'perfect') s.perfect++; else s.good++;
    }
    s.popups.push({ id: note.id, lane: note.lane, text: result === 'perfect' ? 'Perfect!' : result === 'good' ? 'Good' : 'Miss', at: s.now });
  };

  const accuracyOf = (s: typeof g.current) => {
    // Stray presses count against you, so mashing keys doesn't pay off.
    const total = s.perfect + s.good + s.missed + s.strayPresses;
    return total ? Math.round(((s.perfect + s.good) / total) * 100) : 100;
  };

  // Loop: mark notes that slid past the line as missed and end after the last note.
  useEffect(() => {
    if (gameState !== 'playing') return;
    let raf = 0;
    const loop = (now: number) => {
      const s = g.current;
      s.now = now;
      for (const n of s.notes) {
        if (!n.result && now - n.hitAt > GOOD_MS) {
          judge(s, n, 'miss');
          // The tune carries on quietly so the song stays recognisable.
          playMusicNote(n.freq, n.ms / 1000 * 0.9, 0.05);
        }
      }
      s.popups = s.popups.filter(p => now - p.at < 500);
      if (now > s.end + 900) {
        const acc = accuracyOf(s);
        if (acc >= PASS) {
          s.newUnlock = s.song + 1 > unlocked && s.song + 1 < SONGS.length;
          unlock(s.song + 1);
        }
        setGameState('over');
        return;
      }
      setFrame(f => f + 1);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  const song = SONGS[g.current.song];
  const keys = [...song.keys];

  useEffect(() => {
    if (gameState !== 'playing') return;
    const onKey = (e: KeyboardEvent) => {
      if (!isTypingKey(e)) return;
      const lane = keys.indexOf(e.key.toLowerCase());
      if (lane === -1) return;
      e.preventDefault();
      if (e.repeat) return;
      const s = g.current;
      const now = performance.now();
      s.pressed.set(lane, now);
      const note = s.notes
        .filter(n => n.lane === lane && !n.result && Math.abs(now - n.hitAt) <= GOOD_MS)
        .sort((a, b) => a.hitAt - b.hitAt)[0];
      if (note) {
        judge(s, note, Math.abs(now - note.hitAt) <= PERFECT_MS ? 'perfect' : 'good');
        playMusicNote(note.freq, note.ms / 1000 * 0.9);
      } else {
        s.strayPresses++;
        s.combo = 0;
        playWrongSound();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  const s = g.current;
  const accuracy = accuracyOf(s);
  const button = 'bg-gradient-to-r from-fuchsia-500 to-violet-500';

  if (gameState === 'ready') {
    const picked = SONGS[songIdx];
    return (
      <GameIntro icon="🎵" title="Key Beats" skill="rhythm and steady keystrokes on one row at a time." accent="text-fuchsia-600" button={button} onStart={() => startGame()}>
        <p>Play real songs! Notes slide down each lane -- press that lane's key as the note reaches the glowing line and the melody plays. Low notes are on the left, high notes on the right. Score {PASS}% or better to unlock the next song.</p>
        <p className="mt-4 font-bold text-gray-700">Choose a song:</p>
        <LevelPicker levels={SONGS} unlocked={unlocked} selected={songIdx} onSelect={i => { stopPreview(); setSongIdx(i); }} label="SONG" />
        <div className="mt-4 inline-flex flex-wrap items-center justify-center gap-3 px-4 py-3 rounded-2xl bg-white shadow ring-1 ring-fuchsia-200">
          <div className="text-left">
            <div className="font-black text-gray-800">{picked.icon} {picked.name}</div>
            <div className="text-xs text-gray-500">{picked.by} · {picked.row} · keys <span className="font-mono font-bold text-fuchsia-600">{picked.keys.toUpperCase().split('').join(' ')}</span></div>
          </div>
          <button type="button" onClick={() => (previewing ? stopPreview() : preview(songIdx))}
            className="px-3 py-1.5 rounded-full text-sm font-bold bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-200">
            {previewing ? '■ Stop' : '🔊 Listen'}
          </button>
        </div>
      </GameIntro>
    );
  }
  if (gameState === 'over') {
    const stars = starsFor(accuracy);
    const passed = accuracy >= PASS;
    const next = s.song + 1 < SONGS.length ? SONGS[s.song + 1] : null;
    return (
      <GameOver
        title={passed ? `${song.name} ${song.icon}` : 'Keep Practicing!'}
        stars={stars}
        message={!passed ? `Hit ${PASS}% of the notes to unlock the next song.`
          : s.newUnlock && next ? `Song ${s.song + 2} unlocked: ${next.name} ${next.icon}`
          : !next ? 'You played every song -- keyboard maestro! 🏆'
          : accuracy >= 90 ? 'You kept a perfect beat!' : 'Nice rhythm -- keep it up!'}
        stats={[{ label: 'Score', value: s.score }, { label: 'Accuracy', value: `${accuracy}%` }, { label: 'Best Combo', value: s.bestCombo }, { label: 'Perfects', value: s.perfect }]}
        button={button}
        onRestart={() => startGame(s.song)}
      >
        {passed && next && (
          <div>
            <button onClick={() => startGame(s.song + 1)} className="fun-shine mt-6 px-8 py-3 text-white rounded-full font-black text-xl shadow-lg ring-4 ring-white bg-gradient-to-r from-sky-500 to-indigo-500 transition-transform hover:scale-105 active:scale-95">
              Next Song: {next.name} {next.icon} ▶
            </button>
          </div>
        )}
      </GameOver>
    );
  }

  const multiplier = Math.min(4, 1 + Math.floor(s.combo / 10));
  const laneWidth = 100 / keys.length;
  const progress = Math.min(1, Math.max(0, (s.now - s.start) / (s.end - s.start)));
  const laneLabels = [...new Set(s.notes.map(n => n.lane))].reduce<Record<number, string>>((acc, lane) => {
    acc[lane] = s.notes.find(n => n.lane === lane)!.label;
    return acc;
  }, {});
  const introAge = s.now - s.start;

  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-around items-center mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
        <StatBox label="Score" value={s.score} />
        <StatBox label="Accuracy" value={`${accuracy}%`} />
        <StatBox label="Combo" value={s.combo} />
        <StatBox label="Multiplier" value={`x${multiplier}`} />
      </div>
      <div className="flex items-center gap-3 mb-3">
        <span className="shrink-0 text-sm font-black text-fuchsia-700">{song.icon} {song.name}</span>
        <div className="flex-1 h-2 rounded-full bg-fuchsia-100 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-fuchsia-500 to-violet-500" style={{ width: `${progress * 100}%` }} />
        </div>
      </div>
      <div className="relative overflow-hidden rounded-xl border-2 border-fuchsia-900 bg-gradient-to-b from-slate-900 via-purple-950 to-slate-900 select-none" style={{ height: HEIGHT }}>
        {keys.map((k, lane) => {
          const pressedAgo = s.now - (s.pressed.get(lane) ?? -1e9);
          return (
            <div key={k} className="absolute top-0 bottom-0 border-r border-white/5" style={{ left: `${lane * laneWidth}%`, width: `${laneWidth}%` }}>
              {pressedAgo < 120 && <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent" />}
            </div>
          );
        })}
        <div className="absolute left-0 right-0 h-1 bg-fuchsia-400 shadow-[0_0_16px_4px_rgba(232,121,249,0.6)]" style={{ top: HIT_Y }} />

        {s.notes.filter(n => n.result !== 'perfect' && n.result !== 'good' && n.hitAt - s.now < FALL_SECONDS * 1000 + 50 && s.now - n.hitAt < 600).map(n => {
          const y = HIT_Y - (n.hitAt - s.now) * PX_PER_MS;
          // A note head on the line plus a tail showing how long the note lasts.
          const h = Math.max(26, n.ms * PX_PER_MS - 8);
          const finger = KEY_TO_FINGER[keys[n.lane]];
          const color = finger !== undefined ? FINGER_COLORS[finger] : 'bg-fuchsia-400';
          return (
            <div key={n.id} data-note={keys[n.lane]} className={`absolute ${n.result === 'miss' ? 'opacity-30' : ''}`}
              style={{ left: `calc(${n.lane * laneWidth}% + 6px)`, width: `calc(${laneWidth}% - 12px)`, top: y + 13 - h, height: h }}>
              {h > 34 && <div className={`absolute top-0 bottom-5 left-1/2 -translate-x-1/2 w-2.5 rounded-full opacity-60 ${color}`} />}
              <div className={`absolute bottom-0 inset-x-0 h-[26px] rounded-lg shadow-lg ring-2 ring-white/40 flex items-center justify-center text-xs font-black text-black/60 ${color}`}>
                {keys[n.lane].toUpperCase()}
              </div>
            </div>
          );
        })}

        {s.popups.map(p => (
          <div key={`p-${p.id}`} className={`absolute text-center text-sm font-extrabold pointer-events-none ${p.text === 'Miss' ? 'text-red-400' : p.text === 'Good' ? 'text-sky-300' : 'text-yellow-300'}`}
            style={{ left: `${p.lane * laneWidth}%`, width: `${laneWidth}%`, top: HIT_Y - 40 - (s.now - p.at) / 20, opacity: 1 - (s.now - p.at) / 500 }}>
            {p.text}
          </div>
        ))}

        {introAge < 2400 && (
          <div className="absolute inset-x-0 top-24 text-center pointer-events-none" style={{ opacity: Math.min(1, (2400 - introAge) / 600) }}>
            <div className="text-5xl mb-2">{song.icon}</div>
            <div className="text-3xl font-black text-white drop-shadow">{song.name}</div>
            <div className="text-sm font-semibold text-fuchsia-200">{song.by} · get ready!</div>
          </div>
        )}

        {keys.map((k, lane) => {
          const isDown = s.now - (s.pressed.get(lane) ?? -1e9) < 120;
          return (
            <div key={`key-${k}`} className="absolute flex flex-col items-center" style={{ left: `${lane * laneWidth}%`, width: `${laneWidth}%`, top: HIT_Y + 12 }}>
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center font-mono text-xl font-bold border-2 transition-transform ${isDown ? 'scale-90 bg-fuchsia-500 border-fuchsia-300 text-white' : 'bg-white/10 border-white/30 text-white'}`}>{k}</span>
              <span className="text-[10px] font-bold text-fuchsia-200/80 leading-tight">{laneLabels[lane] ?? ''}</span>
            </div>
          );
        })}
      </div>
      <p className="text-center text-sm text-gray-500 mt-3">Keep your fingers on the {song.row.toLowerCase()} and press keys on the line.</p>
    </div>
  );
};

export default KeyBeatsGame;
